// Connected accounts (spec/07 §2 "Account linking", §3.4; F-IAM-03 … F-IAM-05): Google, Discord and Steam on the
// player's account page, each listed only while this deployment has it switched on, with Link or Unlink.
//
//   Linking: from this signed-in session only (`/v1/auth/link-social` for Google and Discord, `/v1/auth/steam/start`
//   with `link: true` for Steam), the provider's round trip coming back to `/account?linked=<provider>`, or with
//   `?error=<code>` to say why not. Never by email.
//
//   Unlinking: confirmed in a centred dialog. The server keeps every account with a way in: it refuses to unlink the
//   last one it counts, the password and the linked providers (`FAILED_TO_UNLINK_LAST_ACCOUNT`), and the page says
//   so before anyone asks, with the same count (`accounts.length > 1`). It also asks for a session signed in within
//   ten minutes (`SESSION_NOT_FRESH`), answered with "sign in again".
//
//   What a row shows: the provider and when it was linked. The api keeps no provider address or display name for a
//   linked account, so none is shown, and an account's own email is never put beside a provider it did not come from
//   (a synthetic `….invalid` address least of all).

import type { LinkedAccount, SocialProvider } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Card, Dialog, ProviderTile, Skeleton, useToast } from '@clavian/ui-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { problemCode } from '../api/problems';
import { queryKeys, useLinkedAccounts, useSocialProviders } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { isSocialProvider, providerNavigation, providerUrl, socialRefusal } from '../auth/social';
import { useSignOut } from '../shell/AppShell';
import { LoadingRegion } from '../shell/feedback';

/**
 * Why a link or an unlink was refused: `stale` (sign in again) and `lastWay` have their own lines and, for `stale`, a
 * button; any other refusal is the words of its problem code.
 */
type RowFailure = { readonly kind: 'stale' | 'lastWay' } | { readonly kind: 'text'; readonly text: string };

/** A refused link, as the copy key under `account.connected.error`: the few that say more than "try again". */
function linkRefusal(refusal: ReturnType<typeof socialRefusal>): 'cancelled' | 'expired' | 'takenByOther' | 'unverified' | 'generic' | undefined {
  if (refusal === undefined) return undefined;
  return refusal === 'cancelled' || refusal === 'expired' || refusal === 'takenByOther' || refusal === 'unverified' ? refusal : 'generic';
}

function rowFailureOf(error: unknown, text: string): RowFailure {
  switch (problemCode(error)) {
    case 'SESSION_NOT_FRESH':
      return { kind: 'stale' };
    case 'FAILED_TO_UNLINK_LAST_ACCOUNT':
      return { kind: 'lastWay' };
    default:
      return { kind: 'text', text };
  }
}

export function ConnectedAccountsSection(): ReactNode {
  const { t } = useTranslation('player');
  const { providers } = useSocialProviders();
  const accounts = useLinkedAccounts(providers.length > 0);
  const search = useSearch({ from: '/app/account' });
  const navigate = useNavigate();
  const { show } = useToast();
  const [returned] = useState(() => ({ linked: search.linked, error: search.error, provider: search.provider }));
  const handled = useRef(false);

  // Back from a provider: say what happened once, then take the codes out of the address bar.
  useEffect(() => {
    if (handled.current || (returned.linked === undefined && returned.error === undefined)) return;
    handled.current = true;
    if (isSocialProvider(returned.linked) && returned.error === undefined) {
      show({ title: t('account.connected.linkedToast', { provider: t(`social.provider.${returned.linked}`) }), tone: 'ok' });
    }
    void navigate({ to: '/account', search: {}, replace: true });
  }, [returned, navigate, show, t]);

  if (providers.length === 0) return null;

  // A refusal names the provider the portal put on its URL; without one, it says only that the link did not work.
  const known = isSocialProvider(returned.provider);
  const refusal = returned.error === undefined ? undefined : known ? linkRefusal(socialRefusal(returned.error)) : 'generic';
  const refusedProvider = known ? t(`social.provider.${returned.provider}`) : t('account.connected.thatProvider');

  return (
    <Card title={t('account.connected.title')} description={t('account.connected.hint')}>
      {refusal === undefined ? null : (
        <p className="pw-alert" role="alert">
          {t(`account.connected.error.${refusal}`, { provider: refusedProvider })}
        </p>
      )}
      {accounts.data === undefined ? (
        accounts.isError ? (
          <div className="pw-notice">
            <p className="pw-alert" role="alert">
              {t('account.connected.unavailable')}
            </p>
            <div>
              <Button variant="secondary" size="sm" onClick={() => void accounts.refetch()}>
                {t('account.connected.retry')}
              </Button>
            </div>
          </div>
        ) : (
          <LoadingRegion>
            <Skeleton shape="text" lines={3} />
          </LoadingRegion>
        )
      ) : (
        <ul className="pw-account__devices pw-connected" aria-label={t('account.connected.listLabel')}>
          {providers.map((provider) => (
            <ConnectedRow
              key={provider}
              provider={provider}
              account={accounts.data.find((one) => one.providerId === provider)}
              // better-auth's own count (`allowUnlinkingAll: false`): the password and every linked provider.
              otherWaysIn={accounts.data.length > 1}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ConnectedRow({
  provider,
  account,
  otherWaysIn,
}: {
  readonly provider: SocialProvider;
  readonly account: LinkedAccount | undefined;
  readonly otherWaysIn: boolean;
}): ReactNode {
  const { t } = useTranslation('player');
  const fmt = useVenueFormatter();
  const api = useApi();
  const problem = useProblem();
  const queryClient = useQueryClient();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { show } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [linking, setLinking] = useState(false);
  const [failure, setFailure] = useState<RowFailure | null>(null);
  const name = t(`social.provider.${provider}`);

  const unlink = useMutation({
    mutationFn: (accountId: string) => api.unlinkAccount({ body: { accountId } }),
    onSuccess: async () => {
      setConfirming(false);
      setFailure(null);
      show({ title: t('account.connected.unlinkedToast', { provider: name }), tone: 'ok' });
      await queryClient.invalidateQueries({ queryKey: queryKeys.linkedAccounts });
    },
    onError: (error) => {
      setConfirming(false);
      setFailure(rowFailureOf(error, problem.text(error, { fallback: t('account.connected.unlinkFailed') })));
    },
  });

  const link = async () => {
    setFailure(null);
    setLinking(true);
    try {
      providerNavigation.assign(await providerUrl(api, provider, { landing: `/account?linked=${provider}`, onError: '/account', link: true }));
    } catch (error) {
      setLinking(false);
      setFailure({ kind: 'text', text: problem.text(error, { fallback: t('account.connected.linkFailed') }) });
    }
  };

  const signInAgain = async () => {
    await signOut().catch(() => undefined);
    await navigate({ to: '/sign-in', search: { redirect: '/account' } });
  };

  return (
    <li className="pw-account__device pw-connected__row">
      <ProviderTile provider={provider} />
      <span className="pw-account__device-main">
        <span className="pw-account__device-name">
          {name}{' '}
          <Badge tone={account === undefined ? 'neutral' : 'ok'} dot>
            {account === undefined ? t('account.connected.notLinked') : t('account.connected.linked')}
          </Badge>
        </span>
        {account === undefined ? null : (
          <span className="pw-subtle">{t('account.connected.linkedOn', { date: fmt.date(account.createdAt, 'medium') })}</span>
        )}
        {account !== undefined && !otherWaysIn ? <span className="pw-subtle">{t('account.connected.onlyWay')}</span> : null}
        {failure === null ? null : (
          <span className="pw-notice" role="alert">
            <span className="pw-alert">{failure.kind === 'text' ? failure.text : t(`account.connected.${failure.kind}`, { provider: name })}</span>
            {failure.kind === 'stale' ? (
              <span>
                <Button variant="secondary" size="sm" onClick={() => void signInAgain()}>
                  {t('account.connected.signInAgain')}
                </Button>
              </span>
            ) : null}
          </span>
        )}
      </span>
      {account === undefined ? (
        <Button variant="secondary" size="sm" busy={linking} onClick={() => void link()}>
          {t('account.connected.link', { provider: name })}
        </Button>
      ) : (
        <Dialog
          title={t('account.connected.confirm.title', { provider: name })}
          description={t('account.connected.confirm.body', { provider: name })}
          open={confirming}
          onOpenChange={setConfirming}
          trigger={
            <Button
              variant="ghost"
              size="sm"
              disabled={!otherWaysIn}
              aria-label={t('account.connected.unlinkLabel', { provider: name })}
            >
              {t('account.connected.unlink')}
            </Button>
          }
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t('account.connected.confirm.cancel')}
              </Button>
              <Button variant="danger" className="clav-button--danger-solid" busy={unlink.isPending} onClick={() => unlink.mutate(account.id)}>
                {t('account.connected.confirm.confirm', { provider: name })}
              </Button>
            </>
          }
        />
      )}
    </li>
  );
}
